import React from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonItemDivider,
  IonItemGroup,
  IonLabel,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { VideoConverterProps } from "../../interfaces/core";
import { AppForm, AppInput, SubmitButton } from "../Form";
import AppSelect from "../Form/AppSelect";
import ResetButton from "../Form/ResetButton";
import AppColorPicker from "../Form/AppColorPicker";
import {
  reds,
  blues,
  greens,
  hands,
  flags,
  fruits,
  possibleEmoji,
  animals,
} from "../../lib/video-converter/emojiLib";
import AppToggle from "../Form/AppToggle";
import { Swiper, SwiperSlide } from "swiper/react";
import { useState } from "react";
import VideoConverterPresets from "./VideoConverterPresets";
import { AppContextProvider } from "../AppContext";
import { useTranslation } from "react-i18next";

const VideoConverterSettings: React.FC<{
  settings: VideoConverterProps;
  handleProjectStore: (e) => void;
  handleDismiss: () => void;
  handleResetDefaultSettings: () => void;
}> = ({
  settings,
  handleProjectStore,
  handleDismiss,
  handleResetDefaultSettings,
}) => {
  const { t } = useTranslation();
  const [swiper, setSwiper] = useState(null);
  const [sliderIndex, setSliderIndex] = useState("0");
  const [isSwiping, setIsSwiping] = useState(false);
  const handleSubmit = (values) => {
    handleProjectStore(values);
  };
  const handleReset = () => {
    handleResetDefaultSettings();
    handleDismiss();
  };
  return (
    <AppContextProvider>
      <IonPage>
        <AppForm
          initialValues={settings}
          onSubmit={handleSubmit}
          validationSchema={null}
        >
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="primary">
                <IonButton onClick={handleReset} className="!font-semibold">
                  {t(`Reset`)}
                </IonButton>
              </IonButtons>
              <IonTitle>{t(`Customize`)}</IonTitle>
            </IonToolbar>
            <IonToolbar>
              <IonSegment
                value={sliderIndex}
                onIonChange={(e) => {
                  swiper && swiper.slideTo(parseInt(e.detail.value));
                  // setSliderIndex(e.detail.value);
                }}
              >
                <IonSegmentButton value="0">
                  <IonLabel>{t(`Custom`)}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="1">
                  <IonLabel>{t(`Presets`)}</IonLabel>
                </IonSegmentButton>
                {/* <IonSegmentButton value="2">
                  <IonLabel>Copy</IonLabel>
                </IonSegmentButton> */}
              </IonSegment>
            </IonToolbar>
          </IonHeader>
          <IonContent color="light">
            <Swiper
              onSlideChange={({ activeIndex }) => {
                setSliderIndex(activeIndex.toString());
                setIsSwiping(false);
              }}
              onSliderMove={() => setIsSwiping(true)}
              onSwiper={(s) => setSwiper(s)}
              allowTouchMove={false}
            >
              <SwiperSlide
                className={`${
                  isSwiping || sliderIndex === "0"
                    ? "max-h-min"
                    : "max-h-0 overflow-hidden"
                }`}
              >
                <div className="p-4 w-full">
                  <IonItemGroup className="item-group-input !bg-transparent">
                    <IonItemDivider className="pl-0 pb-2 !bg-transparent">
                      {t(`Audio`)}
                    </IonItemDivider>
                    <AppToggle name="audio.enabled" label="Use audio" />
                  </IonItemGroup>
                  <IonItemGroup className="item-group-input !bg-transparent">
                    <IonItemDivider className="pl-0 pb-2 !bg-transparent">
                      {t(`Display`)}
                    </IonItemDivider>
                    <AppSelect
                      name="display.dpi"
                      label="DPI"
                      options={[20, 24, 36, 48, 60, 72, 96, 120, 144]}
                    />
                    <AppColorPicker
                      name="display.background"
                      label={"Background Color"}
                    />
                  </IonItemGroup>
                  <p className="item-description">
                    {t(`Video Converter.slower`)}
                  </p>
                  <IonItemGroup className="item-group-input !bg-transparent">
                    <IonItemDivider className="pl-0 pb-2 !bg-transparent">
                      {t(`Output`)}
                    </IonItemDivider>
                    <AppSelect
                      name="pixel.type"
                      label="Pixel Type"
                      options={["emoji", "circle", "character"]}
                    />
                    <AppSelect
                      name="pixel.options.displayMode"
                      label="Rendering Mode"
                      options={["random", "wave", "fixed"]}
                      show={(value, form: VideoConverterProps) =>
                        form.pixel.type === "emoji"
                      }
                    />
                    <AppSelect
                      name="pixel.options.source"
                      label="Emoji Source"
                      options={[
                        reds,
                        blues,
                        greens,
                        hands,
                        flags,
                        fruits,
                        possibleEmoji,
                        animals,
                      ]}
                      show={(value, form: VideoConverterProps) =>
                        form.pixel.type === "emoji"
                      }
                    />
                    <AppColorPicker
                      name="pixel.options.forgroundColor"
                      label={"Circle Color"}
                      show={(value, form: VideoConverterProps) =>
                        form.pixel.type === "circle"
                      }
                    />
                    <AppSelect
                      name="pixel.options.displayMode"
                      label="Display Mode"
                      options={["random", "wave", "fixed"]}
                      show={(value, form: VideoConverterProps) =>
                        form.pixel.type === "character"
                      }
                    />
                    <AppInput
                      name="pixel.options.character"
                      label="Character"
                      placeholder="Separate by comma ','"
                      show={(value, form: VideoConverterProps) =>
                        form.pixel.type === "character"
                      }
                      inputClassName="text-right"
                    />
                    <AppColorPicker
                      name="pixel.options.characterColor"
                      label={"Text Color"}
                      show={(value, form: VideoConverterProps) =>
                        form.pixel.type === "character"
                      }
                    />
                  </IonItemGroup>
                  {/* <IonItemGroup className="item-group-input !bg-transparent pt-8">
                    <IonItem
                      className="item-input"
                      onClick={() => {}}
                      button
                      detail={false}
                    >
                      <IonLabel color="primary">Save as template...</IonLabel>
                    </IonItem>
                  </IonItemGroup> */}
                </div>
              </SwiperSlide>
              <SwiperSlide
                className={`${
                  isSwiping || sliderIndex === "1"
                    ? "max-h-min"
                    : "max-h-0 overflow-hidden"
                }`}
              >
                <div className="p-4 w-full">
                  <VideoConverterPresets setSettings={handleProjectStore} />
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <textarea
                  className="w-full bg-transparent dark:text-gray-50 text-xs font-medium border-none outline-none h-48"
                  readOnly
                  value={JSON.stringify(settings, null, "\t")}
                />
              </SwiperSlide>
            </Swiper>
          </IonContent>
          <IonFooter>
            <IonToolbar className="!px-0">
              <div className="grid grid-cols-2 gap-2 px-2">
                <ResetButton title={t(`Cancel`)} onDismiss={handleDismiss} />
                <SubmitButton title={t(`Save`)} onDismiss={handleDismiss} />
              </div>
            </IonToolbar>
          </IonFooter>
        </AppForm>
      </IonPage>
    </AppContextProvider>
  );
};
export default VideoConverterSettings;
